import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Card, Col, Flex, Input, Row, Select, Space, Spin, Table, Button, message, Empty } from "antd";
import {
    getCalculationBreakdownForCCAHIncentive,
    saveCCAHIncentiveCalculation,
    getCCAHCalculationDetails,
} from "../../service/httpsCalls";
import { buildCcahBreakdownColumns, buildCcahIncentiveColumns } from "../../routes/Billing/tableConfig";
import { fmtMoney } from "../../helpers/commonHelpers";

const { Option } = Select;

type BreakdownLine = {
    key: string;
    seq: string;
    category: string;
    finalBilledAmount: number;
};

export interface IncentiveRow {
    key: string;
    item: string;     // UI label
    name: string;     // canonical: 'Total Overall Trips' | 'Total CCS Trips' | 'Total VIP Trips'
    sla: number;
    qualify: string;
    billedAmount: number;
    incentivePercent: number;   // fraction (e.g., 0.05)
    calculatedIncentive: number;
}

const incentiveMap: Record<number, number> = { 98: 0.07, 99: 0.08, 100: 0.09 };
const vipMap: Record<number, number> = { 98: 0.025, 99: 0.03, 100: 0.35 };

const labelFor = (name: string) => {
    switch (name) {
        case "Total Overall Trips": return "Total Overall Trips (a + b + c + d)";
        case "Total CCS Trips": return "Total CCS Trips (a + c)";
        case "Total VIP Trips": return "Total VIP Trips (b + c)";
        default: return name;
    }
};

type Props = {
    invoiceInfo: { id: number } | null;          // needs .id (ClientInvoiceSID)
    parentCallback?: (payload: any) => void;
    readOnly?: boolean;                          // read-only = two grids + invoice number, no inputs
    refreshToken?: number;
};

// share one in-flight request per SID and cache last result
const inFlightBySid = new Map<number, Promise<any>>();
const savedCacheBySid = new Map<number, any>();

// FIX: move breakdown single-flight + cache to module scope so it actually dedupes in StrictMode and across renders
const breakdownInFlightByInv = new Map<string, Promise<any>>();
const breakdownCacheByInv = new Map<string, any>();
const normalizeInv = (inv: string) => inv.trim().toLowerCase();

const fetchBreakdownShared = (inv: string): Promise<any> => {
    const key = normalizeInv(inv);
    if (breakdownCacheByInv.has(key)) {
        return Promise.resolve(breakdownCacheByInv.get(key));
    }
    let p = breakdownInFlightByInv.get(key);
    if (!p) {
        p = new Promise<any>((resolve) => {
            getCalculationBreakdownForCCAHIncentive(inv, (res: any) => resolve(res));
        })
            .then((res) => {
                // Cache only successful results; if API returns an error shape, don't poison the cache
                if (!res?.hasError) breakdownCacheByInv.set(key, res); // avoid caching errors
                return res;
            })
            .finally(() => breakdownInFlightByInv.delete(key));
        breakdownInFlightByInv.set(key, p);
    }
    return p;
};

export default function CCAHCalculationGrids({ invoiceInfo, parentCallback, readOnly = false, refreshToken = 0 }: Props) {
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [loading, setLoading] = useState(false);

    const [breakdownData, setBreakdownData] = useState<BreakdownLine[]>([]);
    const [billedAmounts, setBilledAmounts] = useState({ overall: 0, vip: 0, ccs: 0 });
    const [rows, setRows] = useState<IncentiveRow[]>([]);

    // Track the invoice numbers that the current grids reflect
    const [savedInvoiceNumber, setSavedInvoiceNumber] = useState<string>("");
    const [breakdownInvoiceNumber, setBreakdownInvoiceNumber] = useState<string>("");

    // Snapshot of original saved rows to detect edits
    const [originalRowsSig, setOriginalRowsSig] = useState<string | null>(null);

    // No-data flag
    const [noData, setNoData] = useState<boolean>(false);

    // track the latest requested breakdown invoice to ignore stale responses
    const lastBreakdownInvKeyRef = useRef<string>("");

    const rowSig = (list: IncentiveRow[]) =>
        JSON.stringify(
            list
                .map(r => ({
                    name: r.name,
                    sla: r.sla,
                    billedAmount: Number(r.billedAmount) || 0,
                    incentivePercent: Number(r.incentivePercent) || 0,
                    calculatedIncentive: Number(r.calculatedIncentive) || 0
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        );

    const recalcRow = (r: IncentiveRow): IncentiveRow => ({
        ...r,
        calculatedIncentive: (Number(r.billedAmount) || 0) * (Number(r.incentivePercent) || 0),
    });

    const seedFromTotals = (overall: number, ccs: number, vip: number): IncentiveRow[] => ([
        {
            key: "1",
            item: labelFor("Total Overall Trips"),
            name: "Total Overall Trips",
            sla: 98,
            qualify: "Yes",
            billedAmount: overall,
            incentivePercent: incentiveMap[98],
            calculatedIncentive: overall * incentiveMap[98],
        },
        {
            key: "2",
            item: labelFor("Total CCS Trips"),
            name: "Total CCS Trips",
            sla: 98,
            qualify: "Yes",
            billedAmount: ccs,
            incentivePercent: vipMap[98],
            calculatedIncentive: ccs * vipMap[98],
        },
        {
            key: "3",
            item: labelFor("Total VIP Trips"),
            name: "Total VIP Trips",
            sla: 98,
            qualify: "Yes",
            billedAmount: vip,
            incentivePercent: vipMap[98],
            calculatedIncentive: vip * vipMap[98],
        },
    ]);

    const handleSLAChange = useCallback((value: number, recordKey: string) => {
        if (readOnly) return;
        setRows(prev =>
            prev.map(row => {
                if (row.key === recordKey) {
                    const newPercent = row.name === "Total Overall Trips"
                        ? incentiveMap[value]
                        : vipMap[value];
                    return recalcRow({ ...row, sla: value, incentivePercent: newPercent });
                }
                return row;
            })
        );
    }, [readOnly]);

    const totalIncentive = rows.reduce((sum, r) => sum + (r.calculatedIncentive || 0), 0);

    const breakdownColumns = React.useMemo(
        () => buildCcahBreakdownColumns(), []
    );

    const incentiveColumns = useMemo(
        () =>
            buildCcahIncentiveColumns({
                readOnly,
                onSLAChange: handleSLAChange,
            }),
        [readOnly, handleSLAChange]
    );

    const breakdownSummary = () => (
        <Table.Summary.Row>
            <Table.Summary.Cell index={0} />
            <Table.Summary.Cell index={1} style={{ textAlign: "right", fontWeight: 600 }}>
                Total Invoice Amount
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right" style={{ fontWeight: 600 }}>
                {fmtMoney(billedAmounts.overall)}
            </Table.Summary.Cell>
        </Table.Summary.Row>
    );

    const incentiveSummary = () => (
        <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5} style={{ textAlign: "right", fontWeight: 600 }}>
                Total Invoice Incentive
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right" style={{ fontWeight: 600 }}>
                {fmtMoney(totalIncentive)}
            </Table.Summary.Cell>
        </Table.Summary.Row>
    );

    /** Apply breakdown into state (handles "no data" case by clearing BOTH grids) */
    const applyBreakdown = (inv: string, res: any) => {

        // ignore stale responses (only accept if this is still the latest requested invoice)
        const key = normalizeInv(inv);
        if (key !== lastBreakdownInvKeyRef.current) return; // stale response; ignore

        if (Array.isArray(res) && res.length === 0) {
            // No data -> hide both grids and show message
            setNoData(true);
            setBreakdownData([]);
            setRows([]); // <- make sure calculation grid is cleared too
            setBilledAmounts({ overall: 0, vip: 0, ccs: 0 });
            setBreakdownInvoiceNumber(inv);
            return;
        }

        setNoData(false);

        const ds: BreakdownLine[] = (res || []).map((x: any, idx: number) => ({
            key: String(idx + 1),
            seq: String.fromCharCode(97 + idx),
            category: x.category,
            finalBilledAmount: Number(x.finalBilledAmount) || 0,
        }));
        setBreakdownData(ds);
        setBreakdownInvoiceNumber(inv);

        type Line = { finalBilledAmount?: number; category?: string };
        const { overall: ob, vip: vb, ccs: cb } = (res as Line[]).reduce(
            (acc, { finalBilledAmount = 0, category = "" }) => {
                const amt = Number(finalBilledAmount) || 0;
                acc.overall += amt;
                if (category === "VIP" || category === "CCS&VIP") acc.vip += amt;
                if (category === "CCS" || category === "CCS&VIP") acc.ccs += amt;
                return acc;
            },
            { overall: 0, vip: 0, ccs: 0 }
        );
        setBilledAmounts({ overall: ob, vip: vb, ccs: cb });
    };

    /** map saved rows -> UI rows for a specific invoice number */
    const mapSavedRowsForInvoice = (savedList: any[], inv: string): IncentiveRow[] => {
        const filtered = (savedList || []).filter(r => {
            const rn = r.InvoiceNumber ?? r.invoiceNumber ?? "";
            return rn && rn.trim().toLowerCase() === inv.trim().toLowerCase();
        });
        return filtered.map((r: any, idx: number) => {
            const canonical = r.Item ?? r.item;
            const pctFraction = (Number(r.IncentivePercent ?? r.incentivePercent ?? 0) || 0) / 100;
            return {
                key: String(idx + 1),
                item: labelFor(canonical),
                name: canonical,
                sla: Number(r.SLA ?? r.sla) || 98,
                qualify: r.Qualify ?? r.qualify ?? "Yes",
                billedAmount: Number(r.BilledAmount ?? r.billedAmount) || 0,
                incentivePercent: pctFraction,
                calculatedIncentive: Number(r.CalculatedIncentive ?? r.calculatedIncentive) || 0,
            };
        });
    };

    /** initial load by SID (saved calc first, then breakdown by that invoice) */
    useEffect(() => {
        const sid = invoiceInfo?.id;
        if (!sid) return;

        // If parent tells us "refresh", blow away cache for this invoice id
        if (refreshToken > 0) {
            savedCacheBySid.delete(sid);
            inFlightBySid.delete(sid);
        }

        let cancelled = false;
        setLoading(true);

        // serve from cache if available
        if (savedCacheBySid.has(sid)) {
            const saved = savedCacheBySid.get(sid);
            // fall through to handler logic using cached data
            handleSavedResponse(saved);
            setLoading(false);
            return;
        }

        // reuse a single in-flight Promise per SID
        let p = inFlightBySid.get(sid);
        if (!p) {
            p = new Promise<any>((resolve) => {
                getCCAHCalculationDetails(sid, (saved: any) => resolve(saved));
            })
                .then((saved) => {
                    // not cache error payloads
                    if (!saved?.hasError) savedCacheBySid.set(sid, saved);
                    return saved;
                })
                .finally(() => {
                    inFlightBySid.delete(sid);
                });
            inFlightBySid.set(sid, p);
        }

        p.then((saved) => {
            if (cancelled) return;
            handleSavedResponse(saved);
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };

        // extracted to keep diff minimal
        function handleSavedResponse(saved: any) {
            const hasErr = saved?.hasError;
            const list: any[] = !hasErr ? (saved || []) : [];

            if (hasErr) {
                return;
            }

            if (list.length > 0) {

                // Set saved invoice number and original signature
                const inv = list[0]?.InvoiceNumber ?? list[0]?.invoiceNumber ?? "";
                const mapped = mapSavedRowsForInvoice(list, inv);

                setRows(mapped);
                if (inv) {
                    setInvoiceNumber(inv);
                    setSavedInvoiceNumber(inv);
                }
                setOriginalRowsSig(rowSig(mapped));
                setNoData(false);

                // Totals from saved rows for initial state
                const overall = mapped.find((m) => m.name === "Total Overall Trips")?.billedAmount || 0;
                const ccs = mapped.find((m) => m.name === "Total CCS Trips")?.billedAmount || 0;
                const vip = mapped.find((m) => m.name === "Total VIP Trips")?.billedAmount || 0;
                setBilledAmounts({ overall, vip, ccs });

                // Load breakdown using the invoice number from saved API
                if (inv) {
                    // record the latest requested invoice and then apply only if still current
                    lastBreakdownInvKeyRef.current = normalizeInv(inv);
                    fetchBreakdownShared(inv.trim()).then((res: any) => {
                        if (res?.hasError) return;
                        applyBreakdown(inv, res);
                    });
                }
            } else {
                // No saved rows yet
                setOriginalRowsSig(null);
                setSavedInvoiceNumber("");
                setNoData(false);
            }
        }
    }, [invoiceInfo?.id, refreshToken]);

    /** Manual breakdown load for editable mode */
    const [manualLoading, setManualLoading] = useState(false);
    const loadBreakdown = () => {
        if (readOnly) return;
        const inv = invoiceNumber.trim();
        if (!inv) {
            message.warning("Enter invoice number to load breakdown.");
            return;
        }

        // HARD RESET both grids before loading a *different* invoice number
        // This prevents stale data flicker or partial resets.
        setNoData(false);
        setBreakdownData([]);
        setRows([]);
        setBilledAmounts({ overall: 0, vip: 0, ccs: 0 });
        setBreakdownInvoiceNumber("");

        // If switching away from saved invoice, clear snapshot so edits compare correctly
        if (inv !== savedInvoiceNumber) {
            setOriginalRowsSig(null);
        }

        setManualLoading(true);

        // record the latest requested invoice before calling
        lastBreakdownInvKeyRef.current = normalizeInv(inv);

        fetchBreakdownShared(inv).then((res: any) => {
            setManualLoading(false);
            if (res?.hasError) {
                const msg = res?.error?.messages?.[0]?.description ?? "Something went wrong!!!";
                parentCallback?.({ isInvalid: true, errorMessage: msg });
                return;
            }

            applyBreakdown(inv, res);

            // try to load saved rows for this SID + invoice number;
            // if found, use them; else seed defaults (only when breakdown has data)
            const sid = invoiceInfo?.id;
            if (!Array.isArray(res) || res.length === 0) {
                // nothing more to do when breakdown is empty;
                return;
            }

            if (!sid) {
                // no SID available � fallback to defaults
                const totals = (res as any[]).reduce(
                    (acc, x) => {
                        const amt = Number(x.finalBilledAmount) || 0;
                        const category = x.category as string;
                        acc.overall += amt;
                        if (category === "VIP" || category === "CCS&VIP") acc.vip += amt;
                        if (category === "CCS" || category === "CCS&VIP") acc.ccs += amt;
                        return acc;
                    },
                    { overall: 0, vip: 0, ccs: 0 }
                );
                setRows(seedFromTotals(totals.overall, totals.ccs, totals.vip));
                return;
            }

            getCCAHCalculationDetails(sid, (saved: any) => {
                const hasErr = saved?.hasError;
                const list: any[] = !hasErr ? (saved || []) : [];
                if (!hasErr) {
                    const mappedSaved = mapSavedRowsForInvoice(list, inv);
                    if (mappedSaved.length > 0) {
                        setRows(mappedSaved);
                        setSavedInvoiceNumber(inv);
                        setOriginalRowsSig(rowSig(mappedSaved));
                        return;
                    }
                }

                // no saved rows for this invoice number -> seed defaults
                const totals = (res as any[]).reduce(
                    (acc, x) => {
                        const amt = Number(x.finalBilledAmount) || 0;
                        const category = x.category as string;
                        acc.overall += amt;
                        if (category === "VIP" || category === "CCS&VIP") acc.vip += amt;
                        if (category === "CCS" || category === "CCS&VIP") acc.ccs += amt;
                        return acc;
                    },
                    { overall: 0, vip: 0, ccs: 0 }
                );
                setRows(seedFromTotals(totals.overall, totals.ccs, totals.vip));
            });
        });
    };

    // Dirty detection
    const currentSig = useMemo(() => rowSig(rows), [rows]);
    const isDirty = useMemo(() => {
        if (originalRowsSig === null) {
            // no saved snapshot -> creating new calc -> allow save if there�s data
            return rows.length > 0;
        }
        return currentSig !== originalRowsSig;
    }, [currentSig, originalRowsSig, rows.length]);

    const sameInvoiceEverywhere = useMemo(() => {
        const inv = invoiceNumber.trim();
        if (!inv) return false;
        return inv === savedInvoiceNumber && inv === breakdownInvoiceNumber;
    }, [invoiceNumber, savedInvoiceNumber, breakdownInvoiceNumber]);

    const disableLoad = readOnly
        ? true
        : (!invoiceNumber.trim() || sameInvoiceEverywhere);

    const disableSave = readOnly
        ? true
        : (sameInvoiceEverywhere && !isDirty) || noData;

    const handleSave = () => {
        if (readOnly || disableSave) return;
        setLoading(true);
        const payload = {
            invoiceNumber,
            clientInvoiceSID: invoiceInfo?.id,
            calculationDetails: rows.map((r) => ({
                item: r.name,
                sla: r.sla,
                billedAmount: r.billedAmount,
                incentivePercent: r.incentivePercent * 100, // store as percent
                calculatedIncentive: r.calculatedIncentive,
            })),
        };
        saveCCAHIncentiveCalculation(payload, (res: any) => {
            setLoading(false);
            if (res?.hasError) {
                const msg = res?.error?.messages?.[0]?.description ?? "Failed to save incentive calculation.";
                parentCallback?.({ isInvalid: true, errorMessage: msg });
            } else {
                // After successful save, treat current rows as the new original snapshot
                setOriginalRowsSig(rowSig(rows));
                setSavedInvoiceNumber(invoiceNumber.trim());
                parentCallback?.({
                    ccahCalcSaved: true,
                    showMessage: true,
                    message: "Incentive calculation saved successfully!",
                });

                // bust saved-details cache so tab re-open fetches fresh data
                const sid = invoiceInfo?.id;
                if (sid) {
                    savedCacheBySid.delete(sid);
                }
            }
        });
    };

    return (
        <Card styles={{ body: { padding: "15px" } }} loading={loading}>
            {/* Read-only header: show invoice number from saved API */}
            {readOnly && savedInvoiceNumber && (
                <>
                    <p
                        style={{
                            fontSize: 14,
                            color: 'rgba(0, 0, 0, 0.85)',
                            marginTop: '0px',
                            fontWeight: 500,
                        }}
                    >
                        Calculation Information
                    </p>
                    <div style={{ marginBottom: 8 }}>
                        <strong>Invoice Number:&nbsp;</strong>{savedInvoiceNumber}
                    </div>
                </>
            )}

            {/* Editable input + Load button (hidden in read-only) */}
            {!readOnly && (
                <Row type="flex" style={{ background: "#fff", marginBottom: 8 }}>
                    <Col span={6} style={{ paddingRight: "0.5rem" }}>
                        <Input
                            value={invoiceNumber}
                            placeholder="Invoice Number"
                            style={{ width: "100%" }}
                            allowClear
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                        />
                    </Col>
                    <Flex justify="end">
                        <Space>
                            <Button
                                type="primary"
                                onClick={loadBreakdown}
                                disabled={disableLoad}
                                loading={manualLoading}
                            >
                                Load
                            </Button>
                        </Space>
                    </Flex>
                </Row>
            )}

            {loading && <Spin style={{ marginTop: 20 }} />}

            {/* No data state: show placeholder instead of grids */}
            {noData ? (
                <Empty description="No data found." style={{ padding: "24px 0" }} />
            ) : (
                (breakdownData.length > 0 || rows.length > 0) && (
                    <>
                        <h4>Breakdown Details</h4>
                        <Table
                            dataSource={breakdownData}
                            columns={breakdownColumns}
                            rowKey="key"
                            pagination={false}
                            bordered
                            size="small"
                            className="compact-table"
                            summary={breakdownSummary}
                        />

                        <h4 style={{ marginTop: 20 }}>Incentive Calculation</h4>
                        <Table
                            dataSource={rows}
                            columns={incentiveColumns}
                            rowKey="key"
                            pagination={false}
                            bordered
                            size="small"
                            className="compact-table"
                            summary={incentiveSummary}
                        />

                        {!readOnly && (
                            <Flex justify="end" style={{ marginTop: 20 }}>
                                <Space>
                                    <Button
                                        onClick={handleSave}
                                        disabled={disableSave}
                                        style={{ backgroundColor: "#ff4d4f", color: "#fff" }}
                                    >
                                        Save
                                    </Button>
                                </Space>
                            </Flex>
                        )}
                    </>
                )
            )}
        </Card>
    );
}